import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiUpload, FiTrash2 } from 'react-icons/fi';
import { SUPPORTED_FILE_TYPES, MAX_FILE_SIZE, formatFileSize } from '../../types/dataRoom';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const UploadIcon = FiUpload as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface UploadFileModalProps {
  folderName: string;
  onClose: () => void;
  onSubmit: (files: File[]) => Promise<void>;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({
  folderName,
  onClose,
  onSubmit,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isUploading) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    },
    [isUploading, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_FILE_TYPES[file.type]) {
      return `"${file.name}": Unsupported file type. Please upload PDF, DOC, DOCX, XLSX, PPT, PPTX, PNG, or JPG files.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}": File size exceeds maximum limit of ${formatFileSize(MAX_FILE_SIZE)}.`;
    }
    return null;
  };

  const addFiles = (newFiles: File[]) => {
    setError(null);
    const errors: string[] = [];
    const validFiles: File[] = [];

    for (const file of newFiles) {
      // Skip duplicates by name
      if (selectedFiles.some(f => f.name === file.name)) continue;
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        validFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFiles(Array.from(files));
    }
    // Reset input so the same file(s) can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      addFiles(Array.from(files));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setIsUploading(true);

    try {
      await onSubmit(selectedFiles);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  const modal = (
    <div className="su-dataroom-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-dataroom-modal" style={{ maxWidth: '480px' }} role="dialog" aria-modal="true" aria-labelledby="upload-file-title">
        <div className="su-dataroom-modal-header">
          <div>
            <h2 id="upload-file-title" className="su-dataroom-modal-title">Upload Files</h2>
            <p className="su-dataroom-modal-subtitle">
              Upload to {folderName}
            </p>
          </div>
          <button
            className="su-dataroom-modal-close"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-dataroom-modal-body">
            {error && (
              <div
                style={{
                  color: '#f87171',
                  fontSize: '14px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  whiteSpace: 'pre-line',
                }}
              >
                {error}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/png,image/jpeg"
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />

            <div
              className={`su-dropzone ${isDragging ? 'dragging' : ''}`}
              onClick={handleDropzoneClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <UploadIcon className="su-dropzone-icon" />
              <div className="su-dropzone-title">Drop your files here</div>
              <div className="su-dropzone-subtitle">or click to browse</div>
              <div className="su-dropzone-hint">
                PDF, DOC, DOCX, XLSX, PPT, PPTX, PNG, JPG supported
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '8px' }}>
                  {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected ({formatFileSize(totalSize)})
                </div>
                <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                      {!isUploading && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#64748b',
                            padding: '4px',
                            marginLeft: '8px',
                            flexShrink: 0,
                          }}
                          aria-label={`Remove ${file.name}`}
                        >
                          <TrashIcon width={14} height={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadProgress && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#94a3b8' }}>
                {uploadProgress}
              </div>
            )}
          </div>

          <div className="su-dataroom-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isUploading || selectedFiles.length === 0}
            >
              {isUploading ? 'Uploading...' : `Upload${selectedFiles.length > 1 ? ` (${selectedFiles.length})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default UploadFileModal;
